import { AlertService } from '@/common/services/alert/alert.service';
import { UrlService } from '@/common/services/url/url.service';
import { ValidationService } from '@/common/services/validation/validation.service';
import { LoginGuestUseCase } from '@/core/domain/user/usecases/loginGuest.usecase';
import { LoginUserUseCase } from '@/core/domain/user/usecases/loginUser.usecase';
import { Component, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';

@Component({
    selector: 'fe-precheckin',
    templateUrl: './precheckin.component.html',
    styleUrls: ['./precheckin.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PrecheckinComponent {
    public breadcrumbs = [
        {
            label: 'fe_breadcrumb_view_pre_checkin',
            url: 'pre-check-in',
        },
    ];

    public credentialsForm!: FormGroup;
    public guestForm!: FormGroup;

    public loadingBtnLogin = false;
    public loadingBtnGuest = false;

    public status: 'initial' | 'registered' | 'guest' = 'initial';

    constructor(
        private formBuilder: FormBuilder,
        private validation: ValidationService,
        private loginGuestUseCase: LoginGuestUseCase,
        private loginUserUseCase: LoginUserUseCase,
        private router: Router,
        private alert: AlertService,
        private translate: TranslateService,
        private url: UrlService,
    ) {
        this.initLogin();
        this.initGuest();
    }

    private initLogin(): void {
        this.credentialsForm = this.formBuilder.group({
            email: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validation.validEmail,
                ]),
            ],
            password: ['', Validators.compose([Validators.required])],
        });
    }

    private initGuest(): void {
        this.guestForm = this.formBuilder.group({
            email: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validation.validEmail,
                ]),
            ],
            reservationCode: ['', Validators.compose([Validators.required])],
        });
    }

    public onClickLogin(): void {
        this.credentialsForm.markAllAsTouched();
        if (!this.credentialsForm.valid) {
            return;
        }
        this.loadingBtnLogin = true;
        this.loginUserUseCase
            .execute({
                email: this.credentialsForm.get('email')?.value,
                password: this.credentialsForm.get('password')?.value,
            })
            .pipe(
                catchError((error) => {
                    this.loadingBtnLogin = false;
                    this.alertWrongData(error);
                    return throwError(() => error);
                }),
            )
            .subscribe(() => {
                this.loadingBtnLogin = false;
                this.router.navigateByUrl(
                    [this.url.format('pre-check-in/reservations')].join('/'),
                );
            });
    }

    public onClickStart(): void {
        this.guestForm.markAllAsTouched();
        if (!this.guestForm.valid) {
            return;
        }
        this.loadingBtnGuest = true;
        this.loginGuestUseCase
            .execute({
                email: this.guestForm.get('email')?.value,
                reservationCode: this.guestForm.get('reservationCode')?.value,
            })
            .pipe(
                catchError((error) => {
                    this.loadingBtnGuest = false;
                    this.alertWrongData(error);
                    return throwError(() => error);
                }),
            )
            .subscribe(() => {
                this.loadingBtnGuest = false;
                this.router.navigateByUrl(
                    [
                        this.url.format('pre-check-in/reservations'),
                        this.guestForm.get('reservationCode')?.value,
                    ].join('/'),
                );
            });
    }

    public onClickBack(): void {
        this.status = 'initial';
    }

    public onClickContinueUser(): void {
        this.status = 'registered';
    }

    public onClickContinueGuest(): void {
        this.status = 'guest';
    }

    public alertWrongData(error: string): void {
        this.alert.open({
            showCancel: false,
            title: this.alert.getErrorTitle(error),
            subtitle: this.alert.getErrorMessage(error),
        });
    }

    public onClickDidPrecheckin(): void {
        this.alert.open({
            showCancel: false,
            title: this.translate.instant(
                'fe_precheckin_alert_did_precheckin_question',
            ),
            subtitle: this.translate.instant(
                'fe_precheckin_alert_did_precheckin_answer',
            ),
        });
    }

    public onClickTips(): void {
        this.alert.open({
            showCancel: false,
            title: this.translate.instant(
                'fe_precheckin_alert_did_precheckin_tips_title',
            ),
            subtitle: this.translate.instant(
                'fe_precheckin_alert_did_precheckin_tips_answer',
            ),
        });
    }
}
